import { types, flow, Instance, IStateTreeNode, getRoot, cast } from 'mobx-state-tree';
import { AxiosResponse } from 'axios';
import { IRootStore } from 'RootStore';
import {
  LDAPUserModel,
  ILDAPUserModel,
  ILDAPViewEndUserModel,
  LDAPViewEndUserModel,
  FilterateRouteModel,
  LDAPUsersModel,
  ILDAPUsersModel,
} from './models/ldapUser.model';
import { LDAPEndUserModel, ILDAPEndUserModel } from './models/ldapUser.model';
import { LDAPDepartmentModel, ILDAPDepartmentModel } from './models/ldapDepartment.model';
import {
  LDAPCredentialsModel,
  ILDAPCredentialsModel,
  updateLDAPPass,
  LDAPPasswordModel,
  ILDAPPasswordModel,
} from './models/ldapCredentials.model';
import { LDAPSyncModel, ILDAPSyncModel } from './models/ldapCredentials.model';
import { LDAPGroupModel, ILDAPGroupModel } from './models/ldapGroup.model';
import {
  LDAPUsersView,
  LDAPDepartmentsView,
  LDAPGroupsView,
  LDAPEndUsersView,
  LDAPViewEndUserView,
  RouteFilterateInterface,
  LDAPSyncView,
} from './types';
import { DepartmentData, UserData, GroupData } from 'components/Forms';
import axios from 'server';
import { EndUserData } from 'components/Forms/UserForm/SubForms/EndUserGenericForm';
import { ViewEndUserData } from 'components/Forms/UserForm/SubForms/ViewEndUseForm/types';
import { toFormatDateTime } from 'utils';

const { model, array, maybe, maybeNull, reference, string } = types;

export const LDAPStore = model('LDAPStore', {
  credentials: maybeNull(LDAPCredentialsModel),
  passwords: maybeNull(LDAPPasswordModel),
  sync: array(LDAPSyncModel),
  users: array(LDAPUserModel),
  user: array(LDAPUsersModel),
  departments: array(LDAPDepartmentModel),
  department: array(LDAPDepartmentModel),
  groups: array(LDAPGroupModel),
  group: array(LDAPGroupModel),
  profileInfo: maybeNull(LDAPViewEndUserModel),
  clickedEndUser: maybeNull(reference(LDAPEndUserModel)),
  clickedUser: maybeNull(reference(LDAPUserModel)),
  clickedDepartment: maybeNull(reference(LDAPDepartmentModel)),
  clickedGroup: maybeNull(reference(LDAPGroupModel)),
  currentBase: string,
  usersKeywordFilter: string,
  departmentsKeywordFilter: string,
  groupsKeywordFilter: string,
  FilterateRoute: FilterateRouteModel,
  updateLDAPPassword: maybe(updateLDAPPass),
})
  .volatile((self) => ({
    matchCurrentBase({
      base,
    }: LDAPDepartmentsView | LDAPUsersView | LDAPEndUsersView | LDAPViewEndUserView | LDAPGroupsView): boolean {
      if (base !== self.currentBase) {
        return false;
      }

      return true;
    },
  }))
  .views((self) => ({
    get allUsersView(): LDAPUsersView[] {
      const allUsersView: LDAPUsersView[] = [];
      for (let i = 0; i < self.users.length; i++) {
        allUsersView.push({ ...self.users[i], id: i });
      }
      return allUsersView;
    },
    get CurrentClickedUser() {
      return self.clickedUser;
    },
    get getUsersKeywordFilter(): string {
      return self.usersKeywordFilter;
    },
    get getFilterateRoute(): RouteFilterateInterface {
      const data = { ...self.FilterateRoute };
      return data;
    },
    get allGroupsView(): LDAPGroupsView[] {
      const allGroupsView: LDAPGroupsView[] = [];
      for (let i = 0; i < self.groups.length; i++) {
        allGroupsView.push({
          ...self.groups[i],
          id: i,
        });
      }
      return allGroupsView;
    },

    get syncs(): LDAPSyncView[] {
      const syncs: LDAPSyncView[] = [];
      for (let i = 0; i < self.sync.length; i++) {
        syncs.push({ ...self.sync[i] });
      }
      return syncs;
    },
    get usersView(): LDAPUsersView[] {
      const usersView: LDAPUsersView[] = [];
      for (let i = 0; i < self.users.length; i++) {
        usersView.push({ ...self.users[i], id: i });
      }
      if (self.currentBase === 'all') {
        return usersView;
      }

      const filteredByBase = usersView.filter(self.matchCurrentBase);
      return filteredByBase;
    },
    get usersData() {
      const data = [...self.users];
      return data;
    },
    get departmentsView(): LDAPDepartmentsView[] {
      const departmentsView: LDAPDepartmentsView[] = [];
      const keywords = self.departmentsKeywordFilter.split(/\s+/).map((keyword) => keyword.trim());
      const regexp = new RegExp(keywords.map((keyword) => `(?=.*${keyword})`).join(''), 'i');
      for (let i = 0; i < self.departments.length; i++) {
        if (self.departments[i].base !== '/') {
          departmentsView.push({ ...self.departments[i], id: i, base: self.departments[i].base || '/' });
        }
      }
      if (self.currentBase === 'all') {
        return departmentsView.filter(({ ou, description, createtimestamp, modifytimestamp }) =>
          regexp.test(`${ou}${description}${toFormatDateTime(createtimestamp)}${toFormatDateTime(modifytimestamp)}`)
        );
      }
      const filteredByBase = departmentsView.filter((department) => self.matchCurrentBase(department));
      if (!self.departmentsKeywordFilter) {
        return filteredByBase;
      }
      return filteredByBase.filter(({ ou, description, createtimestamp, modifytimestamp }) =>
        regexp.test(`${ou}${description}${toFormatDateTime(createtimestamp)}${toFormatDateTime(modifytimestamp)}`)
      );
    },
    get departmentView(): LDAPDepartmentsView[] {
      const departmentsView: LDAPDepartmentsView[] = [];
      const keywords = self.departmentsKeywordFilter.split(/\s+/).map((keyword) => keyword.trim());
      const regexp = new RegExp(keywords.map((keyword) => `(?=.*${keyword})`).join(''), 'i');
      for (let i = 0; i < self.department.length; i++) {
        if (self.department[i].base !== '/') {
          departmentsView.push({ ...self.department[i], id: i, base: self.department[i].base || '/' });
        }
      }
      if (self.currentBase === 'all') {
        return departmentsView.filter(({ ou, description, createtimestamp, modifytimestamp }) =>
          regexp.test(`${ou}${description}${toFormatDateTime(createtimestamp)}${toFormatDateTime(modifytimestamp)}`)
        );
      }
      const filteredByBase = departmentsView.filter((department) => self.matchCurrentBase(department));
      if (!self.departmentsKeywordFilter) {
        return filteredByBase;
      }
      return filteredByBase.filter(({ ou, description, createtimestamp, modifytimestamp }) =>
        regexp.test(`${ou}${description}${toFormatDateTime(createtimestamp)}${toFormatDateTime(modifytimestamp)}`)
      );
    },
    get groupsView(): LDAPGroupsView[] {
      const groupsView: LDAPGroupsView[] = [];
      const regexp = new RegExp(self.groupsKeywordFilter, 'i');
      for (let i = 0; i < self.groups.length; i++) {
        groupsView.push({
          ...self.groups[i],
          id: i,
        });
      }
      if (self.currentBase === 'all') {
        return groupsView.filter(({ cn, description, createtimestamp, modifytimestamp }) =>
          regexp.test(`${cn}${description}${toFormatDateTime(createtimestamp)}${toFormatDateTime(modifytimestamp)}`)
        );
      }
      const filteredByBase = groupsView.filter(self.matchCurrentBase);
      if (!self.groupsKeywordFilter) {
        return filteredByBase;
      }
      return filteredByBase.filter(({ cn, description, createtimestamp, modifytimestamp }) =>
        regexp.test(`${cn}${description}${toFormatDateTime(createtimestamp)}${toFormatDateTime(modifytimestamp)}`)
      );
    },

    get groupView(): LDAPGroupsView[] {
      const groupView: LDAPGroupsView[] = [];
      const regexp = new RegExp(self.groupsKeywordFilter, 'i');
      for (let i = 0; i < self.group.length; i++) {
        groupView.push({
          ...self.group[i],
          id: i,
        });
      }
      if (self.currentBase === 'all') {
        return groupView.filter(({ cn, description, createtimestamp, modifytimestamp }) =>
          regexp.test(`${cn}${description}${toFormatDateTime(createtimestamp)}${toFormatDateTime(modifytimestamp)}`)
        );
      }
      const filteredByBase = groupView.filter(self.matchCurrentBase);
      if (!self.groupsKeywordFilter) {
        return filteredByBase;
      }
      return filteredByBase.filter(({ cn, description, createtimestamp, modifytimestamp }) =>
        regexp.test(`${cn}${description}${toFormatDateTime(createtimestamp)}${toFormatDateTime(modifytimestamp)}`)
      );
    },
  }))
  .actions((self) => ({
    clearClickedUser() {
      self.clickedUser = null;
    },

    clearClickedEndUser() {
      self.clickedEndUser = null;
    },

    clearClickedGroup() {
      self.clickedGroup = null;
    },

    clearClickedDepartment() {
      self.clickedDepartment = null;
    },
  }))
  .actions((self) => ({
    normalizeCredentials({ data }: AxiosResponse): ILDAPCredentialsModel | null {
      self.credentials = data.data;
      return self.credentials;
    },
    normalizePassword({ data }: AxiosResponse): ILDAPPasswordModel | null {
      self.passwords = data.data;
      return self.passwords;
    },
    normalizeSync({ data }: AxiosResponse): ILDAPSyncModel[] {
      self.sync.replace(data.data);
      return self.sync;
    },
    normalizeUsers({ data }: AxiosResponse): ILDAPUserModel[] {
      if (data.data.length === 0) {
        self.clearClickedUser();
      }
      const seenIds = new Set();
      const normalizedData = data.data
        .filter((user: any, index: number) => {
          const id = user.mail || user.uid || `no-email-${index}`;
          if (seenIds.has(id)) {
            return false;
          }
          seenIds.add(id);
          return true;
        })
        .map((user: any, index: number) => ({
          ...user,
          mail: user.mail || user.uid || `no-email-${index}`,
        }));
      self.users.clear();
      self.users.replace(normalizedData);
      return self.users;
    },
    normalizeUser({ data }: AxiosResponse): ILDAPUsersModel[] {
      if (data.data.user) {
        return data.data.user;
      }
      return data.data.user;
    },
    normalizeUserProfileInfo({ data }: AxiosResponse): ILDAPViewEndUserModel | null {
      self.profileInfo = data.data;
      return self.profileInfo;
    },

    normalizeDepartments({ data }: AxiosResponse): ILDAPDepartmentModel[] {
      let departmentsData;
      if (data.data && data.data.department) {
        departmentsData = data.data.department;
      } else if (data.data && data.data.departments) {
        departmentsData = data.data.departments;
      } else if (data.data && data.data.groups) {
        // Handle case where API returns groups and departments together
        departmentsData = data.data.departments || [];
      } else if (data.data && Array.isArray(data.data)) {
        departmentsData = data.data;
      } else if (data && Array.isArray(data)) {
        departmentsData = data;
      } else if (data.data) {
        departmentsData = data.data.departments || data.data.department || [];
      } else {
        console.error('Invalid data structure in response:', data);
        return [];
      }
      if (!Array.isArray(departmentsData)) {
        console.error('departmentsData is not an array:', departmentsData);
        return [];
      }

      // Transform managers from objects to strings while preserving original data
      const normalizedDepartments = departmentsData.map((department: any) => {
        if (department.managers && Array.isArray(department.managers)) {
          // Store original manager objects for UI use
          department.managersData = department.managers;
          // Convert to emails for MobX State Tree compatibility
          department.managers = department.managers.map((manager: any) => {
            if (typeof manager === 'object' && manager.email) {
              return manager.email;
            }
            return typeof manager === 'string' ? manager : '';
          });
        }
        return department;
      });
      self.departments.replace(normalizedDepartments);
      return self.departments;
    },

    normalizeGroups({ data }: AxiosResponse): ILDAPGroupModel[] {
      let groupsData;
      if (data.data.group) {
        groupsData = data.data.group;
      } else if (data.data.groups) {
        groupsData = data.data.groups;
      } else if (data.data) {
        groupsData = data.data;
      } else {
        console.error('Invalid data structure in response');
        return [];
      }

      // Transform managers from objects to strings while preserving original data
      const normalizedGroups = groupsData.map((group: any) => {
        if (group.managers && Array.isArray(group.managers)) {
          // Store original manager objects for UI use
          group.managersData = group.managers;
          // Convert to emails for MobX State Tree compatibility
          group.managers = group.managers.map((manager: any) => {
            if (typeof manager === 'object' && manager.email) {
              return manager.email;
            }
            return typeof manager === 'string' ? manager : '';
          });
        }
        return group;
      });

      self.group.clear();
      self.groups.replace(normalizedGroups);
      return self.groups;
    },

    normalizeGroupsAndDepartments({ data }: AxiosResponse): {
      group: ILDAPGroupModel[];
      department: ILDAPDepartmentModel[];
    } {
      if (data.data.groups) {
        // self.groups.clear();
        self.group.replace(data.data.groups);
      }
      if (data.data.departments) {
        // self.departments.clear();npm
        self.department.replace(data.data.departments);
      }
      return { group: self.group, department: self.department };
    },

    normalizeGroup({ data }: AxiosResponse): ILDAPGroupModel[] {
      if (data && Array.isArray(data.data) && data.data.length > 0) {
        const { group } = data.data[0];
        if (group) {
          return group;
        } else {
          return [];
        }
      } else {
        return [];
      }
    },
    setClickedUser(dn: string): void {
      const foundUser = self.users.find((user) => user.dn.trim().toLowerCase() === dn.trim().toLowerCase());
      if (!foundUser) {
        return;
      }
      self.clickedUser = foundUser;
    },
    setClickedDepartment(dn: string): void {
      const data = self.departments.find((department) => department.dn === dn) || null;
      const departmentForStorage = data
        ? {
          ...data,
          managers: data.managersData || data.managers,
        }
        : null;

      localStorage.setItem('CurrentDepartment', JSON.stringify(departmentForStorage));
      self.clickedDepartment = data;
    },
    setClickedGroup(dn: string): void {
      const data = self.groups.find((group) => group.dn === dn) || self.group.find((group) => group.dn === dn) || null;
      const groupForStorage = data
        ? {
          ...data,
          managers: data.managersData || data.managers,
        }
        : null;

      localStorage.setItem('CurrentGroup', JSON.stringify(groupForStorage));
      self.clickedGroup = data;
    },
    setCurrentBase(base: string): void {
      self.currentBase = base;
    },
    clearSelected(): void {
      self.clickedDepartment = null;
      self.clickedUser = null;
      self.clickedGroup = null;
    },
    setUsersKeywordFilter(filter: string): void {
      self.usersKeywordFilter = filter;
    },
    setDepartmensKeywordFilter(filter: string): void {
      self.departmentsKeywordFilter = filter;
    },
    setFilterateRoute(route: string, filter: string): void {
      const obj = {
        route: route,
        filterString: filter,
      };
      self.FilterateRoute = { ...obj };
    },
    setGroupsKeywordFilter(filter: string): void {
      self.groupsKeywordFilter = filter;
    },
  }))
  .actions((self) => ({
    getCredentials: flow(function* () {
      const result = yield axios
        .get<ILDAPCredentialsModel[]>(`settings/ldap-credentials`)
        .then(self.normalizeCredentials)
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
          throw new Error(errorMessage);
        });
      return result;
    }),

    updateCredentials: flow(function* (password: string) {
      const result = yield axios
        .put<ILDAPDepartmentModel>(`settings/ldap-credentials/1`, { password })
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
          throw new Error(errorMessage);
        });
      return result;
    }),
  }))
  .actions((self) => ({
    getGroupsAndDepartmentsForSelfRegister: flow(function* () {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      const domain = parts.length > 1 && hostname !== 'localhost' ? parts[0] : '';
      const result = yield axios
        .get(`ldap/group-department?domain=${domain}`)
        .then(self.normalizeGroupsAndDepartments)
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
          throw new Error(errorMessage);
        });
      return result;
    }),

    getPassword: flow(function* () {
      const result = yield axios
        .get<ILDAPPasswordModel[]>(`generate-ldap-password`)
        .then(self.normalizePassword)
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
          throw new Error(errorMessage);
        });
      return result;
    }),
    LDAPPassword: flow(function* (password) {
      const result = yield axios
        .post(`update-ldap-password`, password)
        .then((res) => res.data.message)
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
          throw new Error(errorMessage);
        });
      return result;
    }),
    getDepartments: flow(function* () {
      const result = yield axios
        .get<ILDAPDepartmentModel[]>(`departments`)
        .then(self.normalizeDepartments)
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
          throw new Error(errorMessage);
        });
      return result;
    }),

    getDepartment: flow(function* (id) {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      const domain = parts.length > 1 && hostname !== 'localhost' ? parts[0] : '';
      const result = yield axios
        .get<ILDAPDepartmentModel[]>(`user/groups-departments/${id}?domain=${domain}`)
        .then(self.normalizeDepartments)
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
          throw new Error(errorMessage);
        });
      return result;
    }),

    createDepartment: flow(function* (data: DepartmentData) {
      try {
        const result = yield axios.post<ILDAPDepartmentModel>(`/departments`, data);
        if (result.data.message && !result.data.data) {
          return result;
        }
        const apiData = result.data.data;
        if (apiData) {
          self.departments.unshift(apiData);
        }
        return result;
      } catch (error: any) {
        if (error.response) {
          const status = error.response.status;
          const responseData = error.response.data;
          if (status === 422) {
            const errorMessage =
              responseData?.message || responseData?.data?.message || 'Failed to create user due to validation error';
            throw new Error(errorMessage);
          } else if (status === 400) {
            const errorMessage =
              responseData?.error ||
              responseData?.message ||
              responseData?.data?.message ||
              'Failed to create user due to validation error';
            throw new Error(errorMessage);
          } else {
            const errorMessage =
              responseData?.message || responseData?.data?.message || 'Failed to create user due to server error';
            throw new Error(errorMessage);
          }
        } else if (error.message) {
          throw new Error(error.message);
        } else {
          throw new Error('Failed to create user due to an unexpected error');
        }
      }
    }),
    updateDepartment: flow(function* (data: DepartmentData) {
      try {
        const result = yield axios.put<ILDAPDepartmentModel>(`departments/${data?.dn}`, data);
        if (result.data.message && !result.data.data) {
          self.clearSelected();
          return result;
        }
        const updatedData = result.data.data;
        if (updatedData) {
          self.departments = cast(
            self.departments.map((department) => (department.dn === data.dn ? { ...updatedData } : department))
          );
        }
        self.clearSelected();
        return result;
      } catch (error: any) {
        if (error.response) {
          const status = error.response.status;
          const responseData = error.response.data;
          if (status === 422) {
            const errorMessage =
              responseData?.message ||
              responseData?.data?.message ||
              'Failed to update department due to validation error';
            throw new Error(errorMessage);
          } else if (status === 400) {
            const errorMessage =
              responseData?.message ||
              responseData?.data?.message ||
              'Failed to update department due to validation error';
            throw new Error(errorMessage);
          } else {
            const errorMessage =
              responseData?.message || responseData?.data?.message || 'Failed to update department due to server error';
            throw new Error(errorMessage);
          }
        } else if (error.message) {
          throw new Error(error.message);
        } else {
          throw new Error('Failed to update department due to an unexpected error');
        }
      }
    }),
    syncDepartment: flow(function* (data: DepartmentData) {
      const result = yield axios
        .put<ILDAPDepartmentModel>(`admin/app/google/resync/department/${data?.dn}`, data)
        .then(self.clearSelected)
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
          throw new Error(errorMessage);
        });
      return result;
    }),
    deleteDepartment: flow(function* (status: string, appStatus: boolean) {
      try {
        yield axios.delete(
          `departments/${self.clickedDepartment?.dn}?confirm=${status}&appStatus=${appStatus}&orgId=${self.clickedDepartment?.orgId}`
        );
        self.departments = cast(self.departments.filter((department) => department.dn !== self.clickedDepartment?.dn));
        self.clearSelected();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
        throw new Error(errorMessage);
      }
    }),
  }))
  .actions((self) => ({
    endUserSelfRegister: flow(function* (data: UserData) {
      data.cn = `${data.givenname} ${data.sn}`;
      const result = yield axios
        .post<ILDAPUserModel>('admin/ldap/register-new-end-user', data)
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });

      return result;
    }),
    endUserSelfRegisteration: flow(function* (data: EndUserData) {
      const result = yield axios
        .post<ILDAPEndUserModel>('admin/ldap/register-new-end-user', data)
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
      return result;
    }),
    createEndUserStep1: flow(function* (data) {
      const result = yield axios
        .post('admin/ldap/register-new-end-user-step-one', data)
        .then(data)
        .catch((error) => {
          throw new Error(error.response.data.message);
        });
      return result;
    }),
    createNewPassword: flow(function* (data) {
      const result = yield axios
        .post('/new-password', data)
        .then(data)
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
      return result;
    }),
    validateToken: flow(function* (data) {
      const result = yield axios
        .post('/admin/ldap/user/validate-token', data)
        .then(data)
        .catch((error) => {
          throw new Error(error.response.data.data.message || error.response.data.message || error?.message);
        });
      return result;
    }),
    validateTokens: flow(function* (token: string, email: string) {
      const url = `/auth/password/verify/${token}?email=${encodeURIComponent(email)}`;
      const result = yield axios.get(url).catch((error) => {
        const message = error?.response?.data?.message || error?.response?.data?.data?.message ||error?.message ||'Unable to validate token';
        throw new Error(message);
        // throw new Error(error.response.data.data.message || error.response.data.message || error?.message);
      });
      return result;
    }),
    acceptEndUserRegisteration: flow(function* (id: number, data: ViewEndUserData) {
      data.cn = `${data.givenname} ${data.sn}`;
      const result = yield axios
        .post<ILDAPViewEndUserModel>(`admin/accept-new-registered-user/${id}`, data)
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error.response.data.message || error.response.data.message || error?.message);
        });
      return result;
    }),
    getProfileInfo: flow(function* (id: number) {
      const result = yield axios
        .get<ILDAPViewEndUserModel>(`admin/new-registered-user/${id}`)
        .then(self.normalizeUserProfileInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getUsers: flow(function* () {
      let url = 'ldap/users';
      if (self.usersKeywordFilter) {
        url += `?search=${self.usersKeywordFilter}`;
      }
      const result = yield axios
        .get<ILDAPUserModel[]>(url)
        .then(self.normalizeUsers)
        .catch((error) => {
          if (error.response) {
            const status = error.response.status;
            const responseData = error.response.data;
            if (status === 422) {
              const errorMessage =
                responseData?.message || responseData?.data?.message || 'Failed to create user due to validation error';
              throw new Error(errorMessage);
            } else if (status === 400) {
              const errorMessage =
                responseData?.error ||
                responseData?.message ||
                responseData?.data?.message ||
                'Failed to create user due to validation error';
              throw new Error(errorMessage);
            } else {
              const errorMessage =
                responseData?.message || responseData?.data?.message || 'Failed to create user due to server error';
              throw new Error(errorMessage);
            }
          } else if (error.message) {
            throw new Error(error.message);
          } else {
            throw new Error('Failed to create user due to an unexpected error');
          }
        });
      return result;
    }),
    getSync: flow(function* () {
      const result = yield axios
        .get<ILDAPSyncModel[]>(`admin/ldap/ldap-sync-inprogress`)
        .then(self.normalizeSync)
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
          throw new Error(errorMessage);
        });
      return result;
    }),
    createGoogleUser: flow(function* (data: UserData) {
      data.cn = `${data.givenname} ${data.sn}`;
      const result = yield axios
        .put<ILDAPUserModel>(`admin/app/google/resync/user/${data?.dn}`, data)
        .then(self.clearSelected)
        .catch((error) => {
          throw error.response.data;
        });
      return result;
    }),
    createEndUser: flow(function* (data: EndUserData) {
      const result = yield axios.post<ILDAPEndUserModel>('/users/register', data).catch((error) => {
        throw error;
      });
      self.clearSelected();
      return result;
    }),
    createUser: flow(function* (data: UserData) {
      try {
        data.cn = `${data.givenname} ${data.sn}`;
        const result = yield axios.post<ILDAPUserModel>('ldap/users', data);

        // If API only returns message, don't try to add to users array
        if (result.data.message && !result.data.user && !result.data.data) {
          return result;
        }

        const apiData = result.data.user || result.data.data;
        if (!apiData) {
          return result;
        }
        if (apiData.carlicense && apiData.carlicense !== 'EndUser') {
          return self.users;
        }
        const normalizedData = {
          ...apiData,
          dn: apiData.dn || apiData.ldap_dn || '',
          cn: apiData.cn || apiData.name || '',
          sn: apiData.sn || apiData.name?.split(' ').pop() || '',
          mail: apiData.mail || apiData.email || `no-email-${Date.now()}`,
        };
        self.users.unshift(normalizedData);
        return result;
      } catch (error: any) {
        if (error.response) {
          const status = error.response.status;
          const responseData = error.response.data;
          if (status === 422) {
            const errorMessage =
              responseData?.message || responseData?.data?.message || 'Failed to create user due to validation error';
            throw new Error(errorMessage);
          } else if (status === 400) {
            const errorMessage =
              responseData?.error ||
              responseData?.message ||
              responseData?.data?.message ||
              'Failed to create user due to validation error';
            throw new Error(errorMessage);
          } else {
            const errorMessage =
              responseData?.message || responseData?.data?.message || 'Failed to create user due to server error';
            throw new Error(errorMessage);
          }
        } else if (error.message) {
          throw new Error(error.message);
        } else {
          throw new Error('Failed to create user due to an unexpected error');
        }
      }
    }),
    updateUser: flow(function* (data: UserData, password: string) {
      try {
        const result = yield axios.put<ILDAPUserModel>(`ldap/users/${data?.dn}?password=${password}`, data);
        const updatedData = result.data?.data || result.data?.user;
        if (updatedData) {
          self.users = cast(
            self.users.map((user) => {
              if (user.dn === data.dn) {
                const updatedUser = { ...user, ...updatedData };
                if (updatedData && !('displayname' in updatedData)) {
                  delete updatedUser.displayname;
                }
                return updatedUser;
              }
              return user;
            })
          );
        }
        self.clearSelected();
        return result;
      } catch (error: any) {
        if (error.response) {
          const errorMessage =
            error.response.data?.error ||
            error.response.data?.message ||
            error.response.data?.data?.message ||
            'Failed to update user due to validation error';
          throw new Error(errorMessage);
        } else {
          throw new Error(error.message || 'Failed to update user due to an unexpected error');
        }
      }
    }),

    lockUser: flow(function* (dn: string, notifyStatus: boolean, permission: string) {
      const clickedUser = self.users.find((user) => user.dn === dn);
      if (!clickedUser) {
        throw new Error(`Can't find user by this dn: ${dn}`);
      }
      try {
        const result = yield axios.put<ILDAPUserModel>(`ldap/users/${dn}`, {
          base: clickedUser.base,
          userpassword: clickedUser.userpassword,
          locked: !clickedUser.locked,
          notify: notifyStatus,
          updatedBy: permission,
          googleMail: clickedUser.mail,
          appStatus: true,
        });
        self.users = cast(self.users.map((user) => (user.dn === dn ? { ...user, locked: !user.locked } : user)));
        self.clearSelected();
        return result;
      } catch (error) {
        throw new Error('An error occurred during lockUser operation');
      }
    }),

    deleteUser: flow(function* (dn: string, appStatus: boolean, permission: string) {
      try {
        yield axios.delete(`ldap/users/${dn}?appStatus=${appStatus}`);
        self.users = cast(self.users.filter((user) => user.dn !== dn));
        self.clearSelected();
      } catch (error) {
        throw new Error();
      }
    }),
  }))
  .actions((self) => ({
    getGroups: flow(function* () {
      const result = yield axios
        .get<ILDAPGroupModel[]>(`groups`)
        .then(self.normalizeGroups)
        .catch((error) => {
          throw new Error(error || error.response.data.data.message || error.message || error.data.message);
        });

      return result;
    }),
    getGroup: flow(function* (id) {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      const domain = parts.length > 1 && hostname !== 'localhost' ? parts[0] : '';
      const result = yield axios
        .get<ILDAPGroupModel[]>(`user/groups-departments/${id}?domain=${domain}`)
        .then(self.normalizeGroups)
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
          throw new Error(errorMessage);
        });

      return result;
    }),
    getUser: flow(function* (id) {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      const domain = parts.length > 1 && hostname !== 'localhost' ? parts[0] : '';
      const result = yield axios
        .get<ILDAPUserModel[]>(`user/groups-departments/${id}?domain=${domain}`)
        .then(self.normalizeUser)
        .catch((error) => {
          console.error('Error fetching departments:', error.response || error);
          const errorMessage =
            error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
          throw new Error(errorMessage);
        });
      return result;
    }),

    createGroup: flow(function* (data: GroupData) {
      try {
        const result = yield axios.post<ILDAPGroupModel>('groups', { ...data });
        if (result.data.message && !result.data.data) {
          return result;
        }
        const apiData = result.data.data;
        if (apiData) {
          self.groups.unshift(apiData);
        }
        return result;
      } catch (error: any) {
        if (error.response) {
          const status = error.response.status;
          const responseData = error.response.data;
          if (status === 422) {
            const errorMessage =
              responseData?.message || responseData?.data?.message || 'Failed to create user due to validation error';
            throw new Error(errorMessage);
          } else if (status === 400) {
            const errorMessage =
              responseData?.error ||
              responseData?.message ||
              responseData?.data?.message ||
              'Failed to create user due to validation error';
            throw new Error(errorMessage);
          } else {
            const errorMessage =
              responseData?.message || responseData?.data?.message || 'Failed to create user due to server error';
            throw new Error(errorMessage);
          }
        } else if (error.message) {
          throw new Error(error.message);
        } else {
          throw new Error('Failed to create user due to an unexpected error');
        }
      }
    }),
    updateGroup: flow(function* (data: GroupData) {
      try {
        const result = yield axios.put<ILDAPGroupModel>(`groups/${data?.dn}`, data);
        if (result.data.message && !result.data.data) {
          self.clearSelected();
          return result;
        }
        const updatedData = result.data.data;
        if (updatedData) {
          self.groups = cast(self.groups.map((group) => (group.dn === data.dn ? { ...updatedData } : group)));
        }
        self.clearSelected();
        return result;
      } catch (error: any) {
        if (error.response) {
          const status = error.response.status;
          const responseData = error.response.data;
          if (status === 422) {
            const errorMessage =
              responseData?.message || responseData?.data?.message || 'Failed to create user due to validation error';
            throw new Error(errorMessage);
          } else if (status === 400) {
            const errorMessage =
              responseData?.error ||
              responseData?.message ||
              responseData?.data?.message ||
              'Failed to create user due to validation error';
            throw new Error(errorMessage);
          } else {
            const errorMessage =
              responseData?.message || responseData?.data?.message || 'Failed to create user due to server error';
            throw new Error(errorMessage);
          }
        } else if (error.message) {
          throw new Error(error.message);
        } else {
          throw new Error('Failed to create user due to an unexpected error');
        }
      }
    }),

    updateManager: flow(function* (entityType: 'group' | 'department', entityId: number, managerIds: number[]) {
      try {
        const url = entityType === 'group' ? `groups/${entityId}/managers` : `departments/${entityId}/managers`;
        const result = yield axios.post(url, { manager_id: managerIds });
        self.clearSelected();
        return result;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || error?.response?.data?.data?.message || 'Failed to update managers';
        throw new Error(errorMessage);
      }
    }),

    updateApp: flow(function* (entityType: 'group' | 'department', entityId: number, data: { providers: string[] }) {
      try {
        const url = entityType === 'group' ? `groups/${entityId}/apps` : `departments/${entityId}/apps`;

        const result = yield axios.post(url, data);

        self.clearSelected();
        return result;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || error?.response?.data?.data?.message || 'Failed to update apps';

        throw new Error(errorMessage);
      }
    }),
    syncGroup: flow(function* (data: GroupData) {
      const result = yield axios
        .put<ILDAPGroupModel>(`admin/app/google/resync/group/${data?.dn}`, data)
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
      return result;
    }),
    deleteGroup: flow(function* (appStatus) {
      try {
        yield axios.delete(`groups/${self.clickedGroup?.dn}?appStatus=${appStatus}`);
        self.groups = cast(self.groups.filter((group) => group.dn !== self.clickedGroup?.dn));
        self.clearSelected();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.data?.message || 'Delete failed';
        throw new Error(errorMessage);
      }
    }),
    getUserTimeline: flow(function* (userDn: string) {
      try {
        const result = yield axios.get(`ldap/users/${userDn}/timeline`);
        const data = result.data.data || result.data;
        if (data) {
          return data;
        }
        return Array.isArray(data) ? data : [];
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || error?.response?.data?.data?.message || 'Failed to load timeline';
        throw new Error(errorMessage);
      }
    }),

    getGoogleTimeline: flow(function* (header) {
      const url = `apps/${header}/timeline?page=1`;
      try {
        const result = yield axios.get(`${url}`);
        const data = result.data.data || result.data;
        if (data) {
          return data;
        }
        return Array.isArray(data) ? data : [];
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || error?.response?.data?.data?.message || 'Failed to load timeline';
        throw new Error(errorMessage);
      }
    }),

    //     getGoogleTimeline: flow(function* (header) {
    //   const url = `apps/${header}/timeline?page=1`
    //   const result = yield axios
    //     .get<any>(`${url}`)
    //     .then((res) => res)
    //     .catch((error) => {
    //       throw error.response.data;
    //     });
    //   return result;
    // }),

    viewTimelineUser: flow(function* (id: number, header) {
      const url = `ldap/user/${id}`;
      const result = yield axios
        .get<any>(`${url}`)
        .then((res) => res)
        .catch((error) => {
          throw error.response.data;
        });
      return result;
    }),

    viewTimeline: flow(function* (id: number, header) {
      const url = `${header}/${id}`;
      const result = yield axios
        .get<any>(`${url}`)
        .then((res) => res)
        .catch((error) => {
          throw error.response.data;
        });
      return result;
    }),
    viewTimelines: flow(function* (id: number, header) {
      const url = header === 'user' ? `ldap/${header}/${id}` : `${header}/${id}`;
      const result = yield axios
        .get<any>(`${url}`)
        .then((res) => res)
        .catch((error) => {
          throw error.response.data;
        });
      return result;
    }),
    viewGoogleTimelineUser: flow(function* (id: number, header) {
      const url = `ldap/user/${id}`;
      const result = yield axios
        .get<any>(`${url}`)
        .then((res) => res)
        .catch((error) => {
          throw error.response.data;
        });
      return result;
    }),
    viewTimelineAdmin: flow(function* (id: number) {
      const url = `ldap/user/${id}`;
      const result = yield axios
        .get<any>(`${url}`)
        .then((res) => res)
        .catch((error) => {
          throw error.response.data;
        });
      return result;
    }),
    getDepartmentTimeline: flow(function* (departmentDn: string) {
      try {
        const result = yield axios.get(`departments/${encodeURIComponent(departmentDn)}/timeline`);
        const data = result.data.data || result.data;
        if (data) {
          return data;
        }
        return Array.isArray(data) ? data : [];
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || error?.response?.data?.data?.message || 'Failed to load timeline';
        throw new Error(errorMessage);
      }
    }),
    getGroupTimeline: flow(function* (groupDn: string) {
      try {
        const result = yield axios.get(`groups/${encodeURIComponent(groupDn)}/timeline`);
        const data = result.data.data || result.data;
        if (data) {
          return data;
        }
        return Array.isArray(data) ? data : [];
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || error?.response?.data?.data?.message || 'Failed to load timeline';
        throw new Error(errorMessage);
      }
    }),
  }));

export type ILDAPStore = Instance<typeof LDAPStore>;

// eslint-disable-next-line
export const withLDAPStore = (self: IStateTreeNode) => ({
  views: {
    get ldapStore(): ILDAPStore {
      return getRoot<IRootStore>(self).ldapStore;
    },
  },
});
